import { useState, useCallback } from 'react'
import { geocodeAddress, reverseGeocode } from '../services/geocoding'
import { isZipCode } from '../utils/mapUtils'
import { createLogger } from '../utils/logger'

const logger = createLogger('useGeocoding')

/**
 * Custom hook for geocoding operations
 * @returns {Object} - Geocoding functions and state
 */
export function useGeocoding() {
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Forward geocode an address
   */
  const geocode = useCallback(async (address) => {
    if (!address || !address.trim()) {
      setError('Please enter an address, city, or zip code')
      return null
    }

    setIsGeocoding(true)
    setError(null)

    try {
      // For zip codes, search without country code restriction
      // Nominatim's ranking works better this way and correctly identifies US zip codes
      const searchQuery = address.trim()
      
      // Don't use country code restriction - it causes incorrect results for zip codes
      const result = await geocodeAddress(searchQuery, null)
      
      if (!result) {
        setError('Location not found. Please try a different address or zip code.')
        return null
      }

      return result
    } catch (err) {
      logger.error('Geocoding error', err)
      setError('Failed to geocode address. Please try again.')
      return null
    } finally {
      setIsGeocoding(false)
    }
  }, [])

  /**
   * Reverse geocode coordinates to address
   */
  const reverseGeocodeFn = useCallback(async (latitude, longitude) => {
    setIsGeocoding(true)
    setError(null)

    try {
      const result = await reverseGeocode(latitude, longitude)
      
      if (!result) {
        // Fallback to coordinates if reverse geocoding fails
        return {
          formattedAddress: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          fullAddress: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
        }
      }

      return result
    } catch (err) {
      // Only log as warning - this is expected in dev (CORS/rate limiting) and has fallback
      logger.warn('Reverse geocoding unavailable, using coordinates', { 
        lat: latitude.toFixed(4), 
        lng: longitude.toFixed(4) 
      })
      // Return coordinates as fallback
      return {
        formattedAddress: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
        fullAddress: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
      }
    } finally {
      setIsGeocoding(false)
    }
  }, [])

  return {
    geocode,
    reverseGeocode: reverseGeocodeFn,
    isGeocoding,
    error,
    setError
  }
}
